import { useEffect, useRef, useState } from "react";
import { Input, Modal, message } from "antd";
import { useTranslation } from "react-i18next";

import { orpcClient } from "@/lib/orpc-client";
import { getUnlockedSignature, setUnlockedSignature } from "@/lib/signature/cache";
import { sessionEmployeeId } from "@/lib/signature/session";

type OnReady = (image: string) => void;

function errorCode(err: unknown) {
  return (err as { code?: string } | null)?.code;
}

export function useSignatureUnlock({ zIndex }: { zIndex?: number } = {}) {
  const { t } = useTranslation();
  const [signature, setSignature] = useState<string | null>(null);
  const [open, setOpen] = useState(false);
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const onReadyRef = useRef<OnReady | null>(null);

  useEffect(() => {
    let active = true;
    sessionEmployeeId().then((id) => {
      const cached = id ? getUnlockedSignature(id) : undefined;
      if (active && cached) setSignature(cached);
    });
    return () => {
      active = false;
    };
  }, []);

  const request = (onReady?: OnReady) => {
    if (signature) {
      onReady?.(signature);
      return;
    }
    onReadyRef.current = onReady ?? null;
    setPassword("");
    setOpen(true);
  };

  const close = () => {
    setOpen(false);
    onReadyRef.current = null;
  };

  const submit = async () => {
    if (!password || busy) return;
    setBusy(true);
    try {
      const result = await orpcClient.employeeSignature.read({ password });
      const id = await sessionEmployeeId();
      if (id) setUnlockedSignature(id, result.image);
      setSignature(result.image);
      setOpen(false);
      setPassword("");
      message.success(t("docAnnotate.signatureReady"));
      onReadyRef.current?.(result.image);
      onReadyRef.current = null;
    } catch (err) {
      const code = errorCode(err);
      if (code === "SIGNATURE_NOT_UPLOADED") {
        close();
        Modal.warning({
          title: t("docAnnotate.signatureMissingTitle"),
          content: t("docAnnotate.signatureMissing"),
          okText: t("docAnnotate.ok"),
          zIndex,
        });
      } else if (code === "SIGNATURE_PASSWORD_INCORRECT") {
        message.error(t("employee.signature.passwordIncorrect"));
      } else if (code === "FORBIDDEN") {
        message.error(t("employee.signature.forbidden"));
      } else {
        message.error(t("employee.signature.requestFailed"));
      }
    } finally {
      setBusy(false);
    }
  };

  const modal = (
    <Modal
      open={open}
      title={t("docAnnotate.unlockTitle")}
      onCancel={close}
      onOk={submit}
      okText={t("employee.signature.confirm")}
      cancelText={t("employee.edit.cancel")}
      okButtonProps={{ disabled: !password, loading: busy }}
      zIndex={zIndex}
      destroyOnHidden
    >
      <p style={{ fontSize: 13, color: "#888" }}>{t("docAnnotate.unlockHint")}</p>
      <Input.Password
        autoComplete="new-password"
        name="signature-pin"
        autoFocus
        aria-label={t("employee.signature.password")}
        placeholder={t("employee.signature.password")}
        value={password}
        maxLength={128}
        onChange={(e) => setPassword(e.target.value)}
        onPressEnter={submit}
      />
    </Modal>
  );

  return { signature, request, modal };
}
